import React, { useEffect, useState, useRef } from 'react';
import { Collapse } from 'reactstrap';
import { Link } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, CircularProgress } from '@mui/material';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import {
  NOTIFICATION_TYPE_CHAT_MESSAGE,
  NOTIFICATION_TYPE_SECURITY_APPROVAL,
  NOTIFICATION_TYPE_NEW_ORDER
} from 'src/utils/pnd/PndConstants';

// Keyframes for the bell shaking animation
const shakeAnimation = `
  @keyframes bell-shake {
    0% { transform: rotate(0); }
    15% { transform: rotate(15deg); }
    30% { transform: rotate(-15deg); }
    45% { transform: rotate(10deg); }
    60% { transform: rotate(-10deg); }
    75% { transform: rotate(5deg); }
    85% { transform: rotate(-5deg); }
    100% { transform: rotate(0); }
  }
  @keyframes glow-pulse {
    0%   { box-shadow: 0 0 6px 2px rgba(118, 75, 162, 0.5); }
    50%  { box-shadow: 0 0 16px 6px rgba(102, 126, 234, 0.8); }
    100% { box-shadow: 0 0 6px 2px rgba(118, 75, 162, 0.5); }
  }
`;

const TopbarNotification = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNotificationHistoryData, setNotificationHistoryData] = useState([]);
  const [isNoRecordFound, setNoRecordFound] = useState(false);

  const prevCountRef = useRef(0);
  // Using a hosted sound file for better reliability than a long base64 string
  const audioRef = useRef(new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"));

  useEffect(() => {
    let interval;
    if (localStorage.getItem('access_token')) {
      getNotificationHistoryDetails();
      // Poll every 30 seconds for new notifications
      interval = setInterval(() => {
        getNotificationHistoryDetails();
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const currentCount = isNotificationHistoryData?.length || 0;
    if (prevCountRef.current > 0 && currentCount > prevCountRef.current) {
      // Play sound only if count INCREASES and it's not the initial load (prev > 0 check optional based on preference, but sticking to increase)
      // Note: Browsers might block autoplay if no user interaction, but this is usually triggered after user logged in/interacted.
      audioRef.current.play().catch(e => console.log("Audio play failed", e));
    }
    prevCountRef.current = currentCount;
  }, [isNotificationHistoryData]);

  const getNotificationHistoryDetails = async () => {
    try {
      // Don't show loading spinner on background polls (optional refinement: could use a separate loading state or just omit setLoading(true) if data exists)
      // keeping simple for now but avoid flickering if possible. 
      // If we already have data, maybe don't set global loading? 
      // check if isNotificationHistoryData has data
      const isBackgroundPoll = isNotificationHistoryData.length > 0;

      if (!isBackgroundPoll) setLoading(true);

      const response = await PndRestApi.GetNotificationHistory();

      if (response.status === 200 && response.data.status === 200) {
        if (response.data.data.length === 0) {
          setNoRecordFound(true);
        } else {
          setNotificationHistoryData(response.data?.data);
        }
      } else {
        console.log('Not success', response.data?.message);
      }
    } catch (error) {
      console.log('Error while calling getNotificationHistoryDetails', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleNotification = () => {
    setIsCollapsed((prevState) => !prevState);
  };

  const updateReadStatus = async (notificationId) => {
    try {
      setLoading(true);
      const response = await PndRestApi.UpdateNotificationReadStatus(notificationId);
      if (response.status === 200 && response.data.status === 200) {
        console.log('success');
      } else {
        console.log('Not success', response.data?.message);
      }
    } catch (error) {
      console.log('Error while updating notification read status', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (notification) {
      await updateReadStatus(notification?.id);
    }
  };

  const getNotificationRedirectPath = (type) => {
    switch (type) {
      case NOTIFICATION_TYPE_CHAT_MESSAGE:
        return '/web_app/chat';
      case NOTIFICATION_TYPE_SECURITY_APPROVAL:
      case NOTIFICATION_TYPE_NEW_ORDER:
        return '/web_app/order/security_approval';
        case NOTIFICATION_TYPE_NEW_ORDER:
          return '/web_app/pnd_order/order_details';
      default:
        return '#';
    }
  };

  const unreadCount = isNotificationHistoryData?.length || 0;

  return (
    <div className="topbar__collapse">
      <style>{shakeAnimation}</style>
      <button
        className="topbar__btn"
        type="button"
        onClick={toggleNotification}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              right: 4,
              top: 4,
              border: `2px solid #fff`,
              padding: '0 4px',
              background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
              fontWeight: 700,
            },
          }}
        >
          <div style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #35c52d 0%, #a4f774 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: unreadCount > 0 ? 'glow-pulse 1.8s ease-in-out infinite' : 'none',
            transition: 'transform 0.2s',
          }}>
            <NotificationsIcon
              sx={{
                animation: unreadCount > 0 ? 'bell-shake 2s infinite' : 'none',
                transformOrigin: 'top center',
                fontSize: '22px',
                color: '#fff'
              }}
            />
          </div>
        </Badge>
      </button>

      {isCollapsed && (
        <button
          className="topbar__back"
          aria-label="topbar__back"
          type="button"
          onClick={toggleNotification}
        />
      )}

      <Collapse
        isOpen={isCollapsed}
        className="topbar__collapse-content"
        style={{ maxWidth: '350px', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }}
      >
        <div className="topbar__collapse-title-wrap" style={{ padding: '16px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderBottom: '1px solid #ddd' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <NotificationsIcon style={{ color: 'white', marginRight: '8px' }} />
            <p className="topbar__collapse-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: 'white' }}>Notifications</p>
          </div>
          <Badge badgeContent={unreadCount} color="error" showZero max={99} />
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0' }}>
          {loading && isNotificationHistoryData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress size={30} />
            </div>
          )}

          {(!loading || isNotificationHistoryData.length > 0) && !isNoRecordFound && isNotificationHistoryData.length > 0 && (
            isNotificationHistoryData.map((notification) => (
              <div
                className="topbar__collapse-item"
                key={notification?.id}
                onClick={() => handleNotificationClick(notification)}
                style={{
                  cursor: 'pointer',
                  padding: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  transition: 'background-color 0.2s',
                  backgroundColor: notification?.read ? '#fff' : '#f0f9ff' // Highlight unread
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = notification?.read ? '#fff' : '#f0f9ff'}
              >
                <div style={{ flex: 1 }}>
                  <Link
                    to={getNotificationRedirectPath(notification?.typeId)}
                    className="topbar__collapse-message"
                    style={{
                      color: '#333',
                      textDecoration: 'none',
                      fontWeight: notification?.read ? 'normal' : '600',
                      display: 'block',
                      marginBottom: '4px'
                    }}
                  >
                    {notification?.title}
                  </Link>
                  <span style={{ fontSize: '11px', color: '#888' }}>{notification?.timeAgo}</span>
                </div>
                {!notification?.read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#32ca73', marginLeft: '10px' }}></div>}
              </div>
            ))
          )}

          {!loading && isNoRecordFound && (
            <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
              <NotificationsIcon style={{ fontSize: '40px', color: '#ddd', marginBottom: '10px' }} />
              <p style={{ margin: 0 }}>No Notification Found</p>
            </div>
          )}

          {!loading && !isNoRecordFound && isNotificationHistoryData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
              <NotificationsIcon style={{ fontSize: '40px', color: '#ddd', marginBottom: '10px' }} />
              <p style={{ margin: 0 }}>No Notification Found</p>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default TopbarNotification;
